/**
 * @file date-picker/date-picker-input.tsx
 *
 * Text input mode for Date Picker — manual entry in MM/DD/YYYY format.
 * Uses library TextField component.
 *
 * @see https://m3.material.io/components/date-pickers/overview
 */

import * as React from "react";
import { parseDateInput } from "../shared/calendar-model";
import type { DatePickerInputProps } from "./date-picker.types";

/**
 * Text input for Date Picker with MM/DD/YYYY format.
 * Validates realtime, commits on blur or Enter.
 */
export const DatePickerInput: React.FC<DatePickerInputProps> = ({
	value,
	onChange,
	onCommit,
	label = "Enter date",
	error,
	locale,
	className,
}) => {
	const inputId = React.useId();
	const errorId = `${inputId}-error`;
	// Format hint according to locale
	const placeholder = React.useMemo(() => {
		if (locale?.startsWith("vi")) return "DD/MM/YYYY";
		return "MM/DD/YYYY";
	}, [locale]);

	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				const parsed = parseDateInput(value);
				onCommit(parsed);
			}
		},
		[value, onCommit],
	);

	const handleBlur = React.useCallback(() => {
		const parsed = parseDateInput(value);
		onCommit(parsed);
	}, [value, onCommit]);

	// Auto-format: inserts "/" after month and day digits
	const handleChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let raw = e.target.value.replace(/\D/g, ""); // retain numbers only

			if (raw.length > 8) raw = raw.slice(0, 8);

			let formatted = raw;
			if (raw.length > 2) {
				formatted = `${raw.slice(0, 2)}/${raw.slice(2)}`;
			}
			if (raw.length > 4) {
				formatted = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
			}

			onChange(formatted);
		},
		[onChange],
	);

	return (
		<div className={className}>
			<div className="px-6 py-4">
				<div className="relative">
					<label
						htmlFor={inputId}
						className="block text-[length:var(--md-typescale-label-large-size,14px)]
							font-[number:var(--md-typescale-label-large-weight,500)]
							text-m3-on-surface-variant
							mb-1"
					>
						{label}
					</label>
					<input
						id={inputId}
						type="text"
						inputMode="numeric"
						value={value}
						onChange={handleChange}
						onBlur={handleBlur}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						aria-label={label}
						aria-invalid={!!error || undefined}
						aria-describedby={error ? errorId : undefined}
						maxLength={10}
						className="w-full h-14 px-4 rounded-m3-xs
							bg-m3-surface-container-highest
							text-[length:var(--md-typescale-body-large-size,16px)]
							hover:bg-m3-on-surface/8
							text-m3-on-surface
							caret-m3-primary
							placeholder:text-m3-on-surface-variant
							outline-none border-0
							focus:ring-2 focus:ring-m3-primary
							transition-m3-fast-effects"
					/>
					{/* Supporting text / error */}
					{error && (
						<p id={errorId} className="mt-1 text-xs text-m3-error px-1">
							{error}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

DatePickerInput.displayName = "DatePickerInput";
